import { useCallback, useEffect, useState } from "react";
import { reportsApi } from "../api/endpoints/reports";

/**
 * Loads the aggregated "Upcoming Vaccinations Report" payload from
 * ``GET /reports/upcoming-vaccinations`` (scope=upcoming, the default).
 * Single round-trip — no per-child fan-out. Strict forward window only.
 */
export function useUpcomingVaccinationsReport() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await reportsApi.upcomingVaccinations();
            setData(result);
        } catch (e) {
            setError(e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    return { data, loading, error, refetch: load };
}

/**
 * Loads the vaccination action-queue payload (scope=reminders) used by
 * the Reminders → Vaccinations tab. Per spec, only Upcoming + Due rows
 * pass through — Overdue is deliberately excluded.
 */
export function useVaccinationReminders() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await reportsApi.vaccinationReminders();
            setData(result);
        } catch (e) {
            setError(e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    return { data, loading, error, refetch: load };
}

/**
 * Loads the WHO growth-alert reminders payload from
 * ``GET /reports/growth-reminders``. One row per child whose latest
 * measurement crossed a critical clinical band.
 */
export function useGrowthRemindersReport() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await reportsApi.growthReminders();
            setData(result);
        } catch (e) {
            setError(e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    return { data, loading, error, refetch: load };
}
